import type { InputParam } from "../../types/grid.ts";
import { ParamType } from "../../types/grid.ts";
import type { ColumnDescription } from "../../types/session.ts";
import { buildDisplayLines } from "./savedFiltersPanel.tsx";
import { StatusIcon } from "../ui/statusIcon.tsx";
import styles from "./filterChips.module.scss";

interface ChipStripProps {
  /** Column-bound filters (inline chips). */
  filters: InputParam[];
  setFilters: (next: InputParam[]) => void;
  /** Parameter-only filters (dashed context pills). */
  paramFilters: InputParam[];
  setParamFilters: (next: InputParam[]) => void;
  /** Click a pill to open the advanced filter panel focused on that filter. */
  onOpenFilterPanel?: (sColumn: string) => void;
  /**
   * When set, column chips split into "reveal" (main body) + "remove" (✕).
   * Used by the classic filter bar, where the filter field itself lives in the
   * horizontally-scrolling column header and is often off-screen.
   */
  onRevealFilter?: (sColumn: string) => void;
  /** Leading text label, e.g. "Active filters". Keeps the cue off colour alone. */
  label?: string;
  /** "notice" = tinted standalone strip above the grid. Default = inline strip. */
  variant?: "inline" | "notice";
  displayNames: Record<string, string>;
  colInfoMap: Record<string, ColumnDescription>;
  /** CommonData.Common array — used to resolve enum/lookup codes to labels. */
  commonData?: unknown[];
}

/**
 * Active-filter strip shown above the grid. Column-bound filters render as
 * removable inline chips; parameter-only filters render as dashed-border
 * context pills with a separate "scope this view" treatment. Clicking a
 * context pill opens the advanced filter panel focused on that filter.
 */
export function FilterChipStrip({
  filters,
  setFilters,
  paramFilters,
  setParamFilters,
  onOpenFilterPanel,
  onRevealFilter,
  label,
  variant = "inline",
  displayNames,
  colInfoMap,
  commonData,
}: ChipStripProps) {
  const common = commonData as Parameters<typeof buildDisplayLines>[3];
  const chipLines = buildDisplayLines(filters, displayNames, colInfoMap, common);
  const pillLines = buildDisplayLines(paramFilters, displayNames, colInfoMap, common);

  if (chipLines.length === 0 && pillLines.length === 0) return null;

  // Removing a chip marks its entries as ParamType.Unused rather than deleting
  // them. Deleting would make the child filter see value=[] and re-emit its
  // configured default (so "Remove" silently puts the value back). Unused with
  // empty oValue is the "explicitly cleared" state: child preserves empty,
  // `buildDisplayLines` skips Unused so the chip disappears, and
  // `useGridData` strips Unused before sending to the server.
  const removeAt = (source: "filters" | "paramFilters", indices: number[]) => {
    const set = new Set(indices);
    const clearParam = (p: InputParam): InputParam => ({ ...p, Type: ParamType.Unused, oValue: "" });
    if (source === "filters") {
      setFilters(filters.map((p, i) => set.has(i) ? clearParam(p) : p));
    } else {
      setParamFilters(paramFilters.map((p, i) => set.has(i) ? clearParam(p) : p));
    }
  };

  return (
    <div className={`${styles.strip}${variant === "notice" ? ` ${styles.stripNotice}` : ""}`}>
      {label && (
        <span className={styles.stripLabel}>
          <StatusIcon kind="search" size={14} />
          {label}
        </span>
      )}
      {chipLines.map((line, i) => {
        // Split chip: the value lives in a column header that may be scrolled
        // out of sight, so the body scrolls it back into view and only the ✕
        // clears it. Without a reveal handler the whole chip is the remove
        // button (chips mode, where the filter has no on-screen field).
        if (onRevealFilter) {
          const sColumn = filters[line.sourceIndices[0]!]?.sColumn ?? "";
          return (
            <span key={`c-${i}`} className={styles.chipSplit}>
              <button
                type="button"
                className={styles.chipMain}
                title={`Show the ${line.label} filter in the grid`}
                onClick={() => onRevealFilter(sColumn)}
              >
                <span className={styles.chipLabel}>{line.label}:</span>
                <span className={styles.chipValue}>{line.detail}</span>
              </button>
              <button
                type="button"
                className={styles.chipRemove}
                title="Remove filter"
                aria-label={`Remove ${line.label} filter`}
                onClick={() => removeAt("filters", line.sourceIndices)}
              >
                ✕
              </button>
            </span>
          );
        }
        return (
          <button
            key={`c-${i}`}
            type="button"
            className={styles.chip}
            title="Remove filter"
            onClick={() => removeAt("filters", line.sourceIndices)}
          >
            <span className={styles.chipLabel}>{line.label}:</span>
            <span className={styles.chipValue}>{line.detail}</span>
            <span className={styles.chipX} aria-hidden="true">✕</span>
          </button>
        );
      })}
      {pillLines.map((line, i) => {
        const sColumn = paramFilters[line.sourceIndices[0]!]?.sColumn ?? "";
        return (
          <span key={`p-${i}`} className={styles.pill}>
            <button
              type="button"
              className={styles.pillMain}
              title="Open in filter panel"
              onClick={() => onOpenFilterPanel?.(sColumn)}
            >
              <span className={styles.chipLabel}>{line.label}:</span>
              <span className={styles.chipValue}>{line.detail}</span>
            </button>
            <button
              type="button"
              className={styles.pillX}
              title="Remove filter"
              aria-label={`Remove ${line.label} filter`}
              onClick={(e) => { e.stopPropagation(); removeAt("paramFilters", line.sourceIndices); }}
            >
              ✕
            </button>
          </span>
        );
      })}
    </div>
  );
}
